import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import type { ToolView } from "../store/selectors";
import type { ToolSource } from "./convert";

/**
 * One tool call as a sentence ("Read the Jul–Sep analysis"), with a spinner while it runs and no
 * icon otherwise: the sentence says how it went ("Didn't fund …"). The line is text from the
 * server's tool UI, never the tool's name; React renders it inert. `detail` follows it, muted
 * ("Read 1 document · 0.4 s").
 */
export function ToolLine({
  line,
  state,
  detail,
  sources = [],
  children,
}: {
  line: string;
  state: ToolView["state"];
  detail?: string;
  sources?: ToolSource[];
  /** Trailing controls (the dev inspector's icon). */
  children?: ReactNode;
}) {
  const running = state === "running" || state === "retry";
  return (
    <div data-agent-tool-line data-state={state} className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
        {running && <Loader2 className="h-3 w-3 shrink-0 motion-safe:animate-spin" aria-hidden />}
        <span className="min-w-0 break-words">
          <span className={cn(running && "text-foreground")}>{line}</span>
          {detail && ` · ${detail}`}
        </span>
        {children}
      </div>
      {sources.length > 0 && (
        // The pages a web search cites: untrusted links, opened apart from the app.
        <ul className="flex flex-wrap gap-1" aria-label="Sources">
          {sources.map((source) => (
            <li key={source.url}>
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                title={source.title || source.url}
                className="inline-flex max-w-[16rem] items-center truncate rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
              >
                {new URL(source.url).hostname}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
